import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

export default function MeetingCard({ meeting, onSlotClick, currentMemberId, canBook = false, canCancel = true }) {
  const { meeting_id, meeting_date, title, status, slots = [] } = meeting;

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isPast = new Date(meeting_date) < new Date().setHours(0, 0, 0, 0);
  const bookedCount = slots.filter(s => s.status === 'booked' || s.status === 'completed').length;
  const totalSlots  = slots.length;
  const memberSlot  = slots.find(s => s.speaker_id === currentMemberId);

  const STATUS_STYLE = {
    completed: { background: 'var(--color-success-dim)',  color: 'var(--color-success)', border: '1px solid var(--color-success-border)' },
    scheduled:  { background: 'var(--color-warning-dim)',  color: 'var(--color-warning)', border: '1px solid var(--color-warning-border)' },
    cancelled:  { background: 'var(--color-danger-dim)',   color: 'var(--color-danger)', border: '1px solid var(--color-danger-border)'  },
    past:       { background: 'var(--bg-input)',           color: 'var(--text-faint)',   border: '1px solid var(--border-subtle)'        },
  };

  const displayStatus = isPast && status === 'scheduled' ? 'past' : status;
  const STATUS_LABEL = { completed: 'Done', scheduled: 'Upcoming', cancelled: 'Cancelled', past: 'Past' };

  return (
    <div className={clsx('card-paper p-4', isPast && 'opacity-60')}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold break-words" style={{ color: 'var(--text-primary)' }}>{title || 'Weekly Meeting'}</p>
          <p className="text-xs mt-0.5 font-medium" style={{ color: 'var(--text-muted)' }}>{formatDate(meeting_date)}</p>
        </div>
        <span
          className="px-2.5 py-0.5 rounded-pill text-xs font-semibold uppercase tracking-wider flex-shrink-0 whitespace-nowrap"
          style={STATUS_STYLE[displayStatus] || { background: 'var(--accent-dim)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)' }}
        >
          {STATUS_LABEL[displayStatus] || displayStatus}
        </span>
      </div>

      {/* Slots */}
      <div className="space-y-1.5">
        {slots.map((slot) => (
          <SlotRow
            key={slot.slot_id}
            slot={slot}
            canBook={canBook && !isPast && !memberSlot && slot.status === 'open'}
            canCancel={canCancel}
            isOwn={slot.speaker_id === currentMemberId}
            onClick={() => onSlotClick?.(slot)}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="mt-3 pt-3 flex items-center justify-between" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <div className="flex items-center gap-1.5">
          <ProgressDots filled={bookedCount} total={totalSlots} />
          <p className="text-xs font-mono" style={{ color: 'var(--text-faint)' }}>
            {bookedCount} of {totalSlots} filled
          </p>
        </div>
        <Link
          to={`/meetings/${meeting_id}`}
          className="text-xs font-semibold transition-colors flex-shrink-0 whitespace-nowrap"
          style={{ color: 'var(--accent)' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.75'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          Details →
        </Link>
      </div>
    </div>
  );
}

function SlotRow({ slot, canBook, canCancel, isOwn, onClick }) {
  const { slot_number, status, speaker_name, presentation_title } = slot;

  const SLOT_STYLE = {
    open:      { background: 'var(--accent-dim)',         border: '1px dashed var(--border-accent)' },
    booked:    { background: 'var(--color-info-dim)',    border: '1px solid var(--color-info-border)' },
    completed: { background: 'var(--color-success-dim)', border: '1px solid var(--color-success-border)' },
  };

  return (
    <div
      className="flex items-center justify-between gap-2 py-2 px-3 rounded-xl"
      style={SLOT_STYLE[status] || { border: '1px solid var(--border-subtle)' }}
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="text-xs font-mono flex-shrink-0" style={{ color: 'var(--text-faint)' }}>#{slot_number}</span>
        {status === 'open' ? (
          <span className="text-xs italic font-normal" style={{ color: 'var(--text-muted)' }}>Open</span>
        ) : (
          <div className="min-w-0">
            <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
              {speaker_name || 'Speaker'}
              {isOwn && <span className="ml-1 text-xs" style={{ color: 'var(--accent)' }}>(you)</span>}
            </span>
            {presentation_title && (
              <p className="text-xs italic mt-0.5 break-words" style={{ color: 'var(--text-muted)' }}>
                "{presentation_title}"
              </p>
            )}
          </div>
        )}
      </div>

      {canBook && (
        <button onClick={onClick} className="btn-bouncy text-xs px-3 py-2 font-semibold flex-shrink-0">Claim</button>
      )}
      {canCancel && isOwn && status === 'booked' && (
        <button
          onClick={onClick}
          className="text-xs px-3 py-2 rounded-pill font-semibold transition-colors flex-shrink-0"
          style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-danger)'; e.currentTarget.style.borderColor = 'var(--color-danger-border)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
        >
          Cancel booking
        </button>
      )}
      {status === 'completed' && (
        <span className="text-xs font-semibold" style={{ color: 'var(--color-success)' }}>✓ Done</span>
      )}
    </div>
  );
}

function ProgressDots({ filled, total }) {
  return (
    <div className="flex gap-1">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className="w-1.5 h-1.5 rounded-full transition-all"
          style={i < filled
            ? { background: 'var(--accent)', transform: 'scale(1)' }
            : { background: 'var(--border-accent)', transform: 'scale(0.85)' }
          }
        />
      ))}
    </div>
  );
}
